import { useEffect, useState } from "react";
import { X, Download, Share, Plus, Sparkles } from "lucide-react";

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [open, setOpen] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    const dismissed = localStorage.getItem("jx_install_dismissed") === "1";
    if (dismissed) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Show banner regardless (iOS Safari never fires beforeinstallprompt)
    const t = setTimeout(() => setOpen(true), 800);

    const onInstalled = () => {
      setOpen(false);
      localStorage.setItem("jx_install_dismissed", "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(t);
    };
  }, []);

  const install = async () => {
    if (evt) {
      evt.prompt();
      const r = await evt.userChoice;
      if (r.outcome === "accepted") {
        localStorage.setItem("jx_install_dismissed", "1");
        setOpen(false);
      }
      return;
    }
    // No native prompt available — show iOS/Safari manual instructions sheet
    setShowIOSSheet(true);
  };

  const dismiss = () => {
    localStorage.setItem("jx_install_dismissed", "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed top-3 inset-x-0 px-3 z-50 jx-fade-up pointer-events-none"
      >
        <div className="mx-auto w-full max-w-md pointer-events-auto">
          <div className="jx-glass rounded-2xl px-2.5 py-2 flex items-center gap-2 border border-cyan-400/30 shadow-[0_0_30px_rgba(0,229,255,0.25)]">
            <div className="w-9 h-9 shrink-0 rounded-xl overflow-hidden">
              <img src="/icon-192.png" alt="Jixxeo Scan" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-heading font-semibold leading-tight truncate">
                Install Jixxeo Scan
              </p>
              <p className="text-[10px] text-slate-400 leading-tight truncate">
                1-tap launch from your home screen
              </p>
            </div>
            <button
              data-testid="pwa-install-button"
              onClick={install}
              className="shrink-0 px-2.5 h-8 text-xs font-semibold rounded-lg bg-cyan-400 text-slate-950 active:scale-95 inline-flex items-center gap-1"
            >
              <Download size={13} /> Install
            </button>
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="shrink-0 text-slate-500 w-7 h-7 flex items-center justify-center rounded-lg hover:text-slate-300 active:scale-95"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {showIOSSheet && (
        <div
          data-testid="install-ios-sheet"
          className="fixed inset-0 z-[60] flex items-end justify-center"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowIOSSheet(false)}
          />
          <div className="relative w-full max-w-md jx-glass rounded-t-3xl p-5 pb-8 jx-fade-up">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src="/icon-192.png" alt="" className="w-full h-full" />
                </div>
                <div>
                  <p className="font-heading font-bold">Install Jixxeo Scan</p>
                  <p className="text-[11px] text-slate-400 font-mono-data uppercase tracking-widest">
                    {isIOS() ? "iOS Safari" : "Add to Home Screen"}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowIOSSheet(false)} className="text-slate-400 p-1">
                <X size={20} />
              </button>
            </div>

            <ol className="mt-4 space-y-3 text-sm text-slate-200">
              <li className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center text-[11px] font-mono-data">
                  1
                </span>
                <span className="inline-flex items-center gap-1.5">
                  Tap the <Share size={16} className="text-cyan-300" /> Share button in your browser toolbar.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center text-[11px] font-mono-data">
                  2
                </span>
                <span className="inline-flex items-center gap-1.5">
                  Choose <strong className="font-heading">Add to Home Screen</strong>
                  <Plus size={14} className="text-cyan-300" />
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center text-[11px] font-mono-data">
                  3
                </span>
                <span className="inline-flex items-center gap-1.5">
                  Tap <strong className="font-heading">Add</strong> — launch Jixxeo
                  <Sparkles size={14} className="text-cyan-300" />
                </span>
              </li>
            </ol>

            <button
              data-testid="pwa-install-ios-ok"
              onClick={() => {
                localStorage.setItem("jx_install_dismissed", "1");
                setShowIOSSheet(false);
                setOpen(false);
              }}
              className="mt-5 w-full h-11 rounded-xl bg-cyan-400 text-slate-950 font-semibold active:scale-95"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
